/**
 * Creating, joining and unlocking a family.
 *
 * This is where the crypto meets the API. Everything that leaves here is
 * already ciphertext or a wrapped key; nothing readable crosses the line.
 *
 * ONE SECRET PER FAMILY: the family code. It is what you join with, what you
 * unlock a new device with, and what Settings shows any member on demand. It
 * replaced a four-word phrase per parent and an invitation that died in an
 * hour -- see DECISIONS.md section 18 for why that reverses sections 11 and
 * 12, and why a secret that has to be SENT is the feature rather than the
 * flaw.
 *
 * The shape, which has not changed:
 *
 *   family key (random AES-256-GCM, generated in this browser)
 *     |
 *     +-- wrapped under the FAMILY CODE, on META as `keyWrap`
 *     +-- wrapped under a display's link code, in that code's `kb`
 *
 * The server holds the wrapping and `sha256` of the code, and can open
 * neither. That is the only arrangement in which "anybody with the code can
 * read the names" and "the server can never read the names" are both true.
 */

import { ApiError, api } from '../api/client';
import {
  createFamilyKey,
  decryptField,
  encryptField,
  openWithSecret,
  packWrapped,
  sealWithSecret,
  unpackWrapped,
  unwrapFamilyKey,
  wrapFamilyKey,
} from '../crypto/familyKey';
import { signedInEmail } from '../auth/cognito';
import { rememberKey } from '../crypto/vault';
import { runSetup } from './state';
import { publicOrigin } from '../native/platform';

export interface FamilySummary {
  id: string;
  since?: string;
  /**
   * The family key wrapped under the family CODE. Absent only for a family
   * whose sign-up stopped before it was given a code; see `hasNoCode`.
   */
  keyWrap?: string | null;
}

export async function listFamilies(): Promise<FamilySummary[]> {
  const result = await api.get<{ families: FamilySummary[] }>('/v1/families');
  return result.families;
}

/**
 * True for a family with no family code at all -- a sign-up that stopped
 * between making the family and giving it a code. The first unlocked device
 * gives it one (App.tsx), because only a device holding the key can.
 */
export function hasNoCode(family: FamilySummary): boolean {
  return !family.keyWrap;
}

export interface CreatedFamily {
  id: string;
  key: CryptoKey;
  /** Shown once on the way into the valley, and in Settings forever after. */
  code: string;
}

/**
 * Make a family, and land in the valley.
 *
 * Four calls, and each one needs something the one before it produced:
 *
 *   POST /families           the family and this parent's membership
 *   POST /families/{id}/code the server chooses the code
 *   PUT  /families/{id}/code everything sealed under it; now joinable
 *   POST /families/{id}/setup   the API refuses every other write until this
 *
 * The setup call used to be a screen, asking for the family's name a second
 * time and for two amounts. It runs here with zeros instead: the monthly
 * deposit is an ordinary setting and starting money is an ordinary entry, so
 * neither is worth standing between a parent and their valley.
 */
export async function createFamily(name: string): Promise<CreatedFamily> {
  const key = await createFamilyKey();
  const [nameCt, emailCt] = await Promise.all([
    encryptField(key, name),
    sealOwnEmail(key),
  ]);

  const { id } = await api.write<{ id: string }>('POST', '/v1/families', {
    nameCt,
    ...(emailCt ? { emailCt } : {}),
  });

  const code = await mintFamilyCode(id, key, name);
  await ensureSetUp(id, key, name, code);

  await rememberKey(id, key);
  return { id, key, code };
}

/**
 * Run setup, if it has not run. The API refuses every other write until then.
 *
 * This used to be a screen, and deleting it is most of what makes "sign up,
 * type a name, land in the valley" true. It asked for the family's name a
 * second time and for two amounts, both of which turn out to be ordinary
 * things: the monthly deposit is a setting and starting money is an entry.
 * Neither is worth standing between a parent and their valley.
 *
 * A 409 is success. It means somebody else already did it -- two parents
 * creating at once, or a create retried after its response went missing --
 * and "your garden is already set up" is the right answer to the wrong
 * question. The family IS ready, which is all this wanted to find out.
 */
export async function ensureSetUp(
  familyId: string,
  key: CryptoKey,
  name: string,
  code?: string,
): Promise<void> {
  try {
    await runSetup(familyId, key, {
      name,
      monthly: '0',
      seed: '0',
      ...(code ? { preview: await sealNameUnderCode(name, code) } : {}),
    });
  } catch (problem) {
    if (!(problem instanceof ApiError) || problem.status !== 409) throw problem;
  }
}

/**
 * Make sure a family has a code, and return it. Idempotent from the caller's
 * point of view even though it always mints a fresh one.
 *
 * Two calls, and the ordering is not an accident. The client cannot wrap the
 * family key until it knows the code, and the SERVER chooses the code: the
 * twenty-character length is justified by an offline attack on `keyWrap` in a
 * stolen table, and that argument only holds while the entropy is picked
 * server-side. See DECISIONS.md section 11, which survived section 18 intact.
 *
 * Three things go up in the second call, sealed three different ways:
 *
 *   keyWrap  the family key, wrapped under the CODE   -- so joining works
 *   codeCt   the code, encrypted under the family KEY -- so Settings can show it
 *   preview  the family's name, sealed under the CODE -- so a link can name it
 *
 * The third is under the code rather than the key because the person who
 * needs to read it is somebody who has not joined, and by definition does not
 * hold the family key yet. That is the whole point of showing it to them
 * first.
 */
export async function mintFamilyCode(
  familyId: string,
  key: CryptoKey,
  name: string,
): Promise<string> {
  const minted = await api.write<{ code: string; display: string }>(
    'POST',
    `/v1/families/${familyId}/code`,
    {},
  );
  const code = normaliseFamilyCode(minted.code);

  const [keyWrap, codeCt, preview] = await Promise.all([
    wrapFamilyKey(key, code).then(packWrapped),
    encryptField(key, code),
    sealNameUnderCode(name, code),
  ]);

  await api.write('PUT', `/v1/families/${familyId}/code`, {
    code,
    keyWrap,
    codeCt,
    preview,
  });
  return code;
}

/**
 * Change the code. Old links stop working; unlocked devices do not notice.
 *
 * The same two calls as minting the first one, because they are the same
 * operation -- the server's transaction is what makes the swap atomic, and
 * this side cannot tell the difference.
 *
 * What this does NOT do is rotate the family key. A device that already holds
 * it carries on reading, which is what anybody expects from changing a lock
 * on their own house. Taking somebody's access away is removing them from the
 * members list; the two controls do different jobs and Settings says so.
 */
export const rotateFamilyCode = mintFamilyCode;


export interface FamilyPreview {
  /** The opaque family id, so the app can tell a stranger from a member. */
  id: string;
  /** The family's own name. Empty if it was never set. */
  family: string;
}

/**
 * What a code is for, without joining with it.
 *
 * Joining is not a small step -- afterwards this parent reads the family's
 * whole ledger -- and a membership is much harder to undo than a confirmation
 * is to add. So the code is checked first, and the answer is decrypted here
 * with the same code: the server returns bytes it has never been able to read.
 *
 * A family migrated from the old scheme before previews existed may have no
 * name sealed under its code. That is not an error, it just means there is
 * nothing to show, and joining still works.
 */
export async function previewFamily(code: string): Promise<FamilyPreview> {
  const normalised = normaliseFamilyCode(code);
  const result = await api.write<{ familyId: string; preview?: string | null }>(
    'POST',
    '/v1/families/join/preview',
    { code: normalised },
  );
  if (!result.preview) return { id: result.familyId, family: '' };
  const opened = JSON.parse(
    await openWithSecret(result.preview, normalised),
  ) as { family?: unknown };
  return {
    id: result.familyId,
    family: typeof opened.family === 'string' ? opened.family : '',
  };
}

/**
 * Join a family with its code -- or unlock this device with it.
 *
 * ONE function for both, because the server makes them one call. A stranger
 * becomes a member; somebody who is already in gets the same wrapping back
 * and nothing is written. Neither side needs to know which happened: what the
 * caller wanted was the family key, and either way it has it.
 *
 * That collapse is most of what the family code bought. "Sign in on a new
 * laptop" and "add my partner" used to be two secrets, two screens and two
 * server mechanisms, and they are now the same field.
 */
export async function joinFamily(code: string): Promise<CreatedFamily> {
  const normalised = normaliseFamilyCode(code);

  const result = await api.write<{
    familyId: string;
    keyWrap?: string | null;
    needsEmail?: boolean;
  }>('POST', '/v1/families/join', { code: normalised });

  if (!result.keyWrap) {
    throw new Error(
      'That family has no family code set up yet, so the names and characters ' +
        'would be unreadable. Ask another parent to open Birbly once on a ' +
        'device that is already unlocked.',
    );
  }

  const key = await unwrapFamilyKey(unpackWrapped(result.keyWrap), normalised);
  await rememberKey(result.familyId, key);

  // The second half, and only on a first join. This device could not have
  // sealed its email under the family key a moment ago, because getting the
  // family key is what the call above was for. The server says `needsEmail`
  // while the membership row has none; an account with no email claim and a
  // device merely unlocking both fall straight through.
  //
  // Deliberately after `rememberKey`: if this fails, the parent is in and
  // unlocked with one blank row in the members list, which is a cosmetic
  // problem. Doing it first would let a cosmetic problem fail a join.
  if (result.needsEmail) {
    const emailCt = await sealOwnEmail(key);
    if (emailCt) {
      try {
        await api.write('POST', '/v1/families/join', {
          code: normalised,
          emailCt,
        });
      } catch {
        // See above. The members list shows them without an address until
        // they next sign in on a new device, which fills it in.
      }
    }
  }

  return { id: result.familyId, key, code: normalised };
}

/**
 * The current code, read back from `codeCt` on the family's own settings.
 *
 * Any unlocked device can do this, including one that did not mint the code
 * and one belonging to a parent who was not there when it was rotated. That
 * is what makes "what's our family code?" a question with an answer.
 *
 * It adds no exposure over what the caller already has: reading `codeCt`
 * needs a membership to fetch it and the family key to open it, and anybody
 * holding both already reads every name in the family. The server holds only
 * `sha256` of the code and could not show it to anybody.
 */
export async function readFamilyCode(
  familyId: string,
  key: CryptoKey,
): Promise<string | null> {
  const state = await api.get<{ settings?: { codeCt?: string | null } }>(
    `/v1/families/${familyId}/state`,
  );
  const sealed = state.settings?.codeCt;
  if (!sealed) return null;
  try {
    return normaliseFamilyCode(await decryptField(key, sealed));
  } catch {
    // Written under a key this device does not hold, or not a code at all.
    // Rotating produces a fresh one, which is the offer Settings makes.
    return null;
  }
}

export interface Member {
  sub: string;
  since?: string;
  /** Decrypted here. Empty for somebody who joined without leaving one. */
  email: string;
}

/** Who is in the family. The list a durable shared secret has to come with. */
export async function listMembers(
  familyId: string,
  key: CryptoKey,
): Promise<Member[]> {
  const result = await api.get<{
    members: { sub: string; since?: string; emailCt?: string | null }[];
  }>(`/v1/families/${familyId}/members`);
  return Promise.all(
    result.members.map(async ({ emailCt, ...member }) => ({
      ...member,
      email: emailCt ? await readOrBlank(key, emailCt) : '',
    })),
  );
}

export async function removeMember(familyId: string, sub: string): Promise<void> {
  await api.write('DELETE', `/v1/families/${familyId}/members/${sub}`);
}

/**
 * The link that takes somebody from a message to the valley.
 *
 * The code goes in the FRAGMENT, never the query or the path. A fragment is
 * not sent to CloudFront, so it never lands in an access log; it is not in
 * the `Referer` of anything the page later loads; and it does not survive
 * into a server-side redirect. The path and the query would fail all three.
 */
export function joinLink(code: string): string {
  return `${publicOrigin()}/join#code=${formatFamilyCode(code)}`;
}

/**
 * Grouped in fours, matching `pretty()` on the server so the same code reads
 * the same wherever it is shown. Presentation only: every hyphen is stripped
 * again by `normaliseFamilyCode` on the way back in, so it does not matter
 * whether they get typed.
 */
export function formatFamilyCode(code: string): string {
  return (code.match(/.{1,4}/g) ?? []).join('-');
}

/** The family code, however somebody typed it. 20 Crockford characters. */
export function normaliseFamilyCode(code: string): string {
  const cleaned = code
    .toUpperCase()
    .replace(/[\s-]/g, '')
    // The three glyphs people actually mistype, folded the way the server
    // folds them before it hashes: I and L read as 1, O reads as 0.
    .replace(/[IL]/g, '1')
    .replace(/O/g, '0');
  if (!/^[0-9ABCDEFGHJKMNPQRSTVWXYZ]{20}$/.test(cleaned)) {
    throw new Error('That family code is not right. Check it and try again.');
  }
  return cleaned;
}

/**
 * This parent's own sign-in email, sealed under the family key.
 *
 * It rides on their membership row so that the OTHER members can see who is
 * in the family. Sealed on this device, so the server stores bytes with no
 * key for them, exactly as it does for every name in the app.
 *
 * Null rather than throwing when there is no email claim. A members list with
 * one blank row is better than a join that fails.
 */
export async function sealOwnEmail(key: CryptoKey): Promise<string | null> {
  const email = signedInEmail();
  return email ? encryptField(key, email) : null;
}

/**
 * The family's name, sealed under the family CODE.
 *
 * `nameCt` is the same string sealed under the family KEY, and they have to
 * agree. A rename that updated only the first would leave a join link
 * announcing the old name to the one person who cannot check it: somebody
 * who holds the code and has not joined yet.
 */
export async function sealNameUnderCode(
  name: string,
  code: string,
): Promise<string> {
  return sealWithSecret(JSON.stringify({ family: name }), code);
}

async function readOrBlank(key: CryptoKey, sealed: string): Promise<string> {
  try {
    return await decryptField(key, sealed);
  } catch {
    return '';
  }
}
